import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import AddIcon from '@mui/icons-material/Add';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import GridViewRoundedIcon from '@mui/icons-material/GridViewRounded';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import PauseCircleOutlineIcon from '@mui/icons-material/PauseCircleOutline';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { deleteOffer, duplicateOffer, getMyImages, getMyOffers, Offer, OFFER_PHOTO_CATEGORY, saveOffer, venueImageUrl, VenueImage, OfferDraft } from '../../api';
import OfferEditor from './OfferEditor';
import { EditLink, errorMessage, Notice, NoticeBox, OfferRow, offerToInput, PageHead, usePortal } from './shared';

const TABS = ['All', 'Active', 'Needs Attention', 'Draft', 'Expired'] as const;
// "Needs Attention": offers hidden from customers for a reason the partner can act on.
const inTab = (o: Offer, tab: Tab) =>
  tab === 'All' || (tab === 'Needs Attention'
    ? ['NeedsCategory', 'Pending', 'Rejected', 'Suspended'].includes(o.status) || !!o.hiddenReason
    : o.status === tab);
type Tab = (typeof TABS)[number];

// Per-offer "..." menu: duplicate, pause/resume, delete.
function MoreMenu({ offer, busy, onDuplicate, onToggleActive, onDelete }: {
  offer: Offer; busy: boolean; onDuplicate: () => void; onToggleActive: () => void; onDelete: () => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => { if (!ref.current?.contains(e.target as Node)) setOpen(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);
  const run = (action: () => void) => () => { setOpen(false); action(); };
  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button type="button" className="pp-icon-btn" disabled={busy} aria-label={`More actions for ${offer.title}`} aria-expanded={open} onClick={() => setOpen((o) => !o)}><MoreHorizIcon /></button>
      {open && (
        <div className="pp-dropdown" role="menu">
          <button role="menuitem" onClick={run(onDuplicate)}><ContentCopyIcon />Duplicate</button>
          {offer.isPublished && offer.status !== 'Expired' && (
            <button role="menuitem" onClick={run(onToggleActive)}>
              {offer.isActive ? <><PauseCircleOutlineIcon />Pause offer</> : <><PlayCircleOutlineIcon />Resume offer</>}
            </button>
          )}
          <button role="menuitem" className="danger" onClick={run(onDelete)}><DeleteOutlineIcon />Delete</button>
        </div>
      )}
    </div>
  );
}

// Venue photos under the editor, as in the proposal: a strip plus shortcuts to the full gallery.
function VenueGalleryStrip() {
  const { to } = usePortal();
  const [images, setImages] = useState<VenueImage[] | null>(null);
  useEffect(() => {
    getMyImages().then((all) => setImages(all.filter((i) => i.category !== OFFER_PHOTO_CATEGORY))).catch(() => setImages([]));
  }, []);
  return (
    <section className="pp-panel">
      <div className="pp-strip-head">
        <div><h3>Venue Image Gallery</h3><small>Manage your venue photos to showcase your space</small></div>
        <Link className="pp-btn pp-gold-outline pp-sm" to={to('/partner-account/gallery')}><GridViewRoundedIcon />Manage Venue Gallery</Link>
      </div>
      <div className="pp-strip">
        {(images ?? []).slice(0, 5).map((img) => <img key={img.id} src={venueImageUrl(img.url)} alt={img.category} loading="lazy" />)}
        <Link className="pp-strip-add" to={to('/partner-account/gallery')}><AddIcon />Add Photos</Link>
      </div>
    </section>
  );
}

// My Offers: tabs and offer list on the left, the Add/Edit panel and venue gallery on the right.
// Routes: /offers (panel closed), /offers/new, /offers/:id/edit.
const MyOffersPage: React.FC = () => {
  const { account, to, refreshDashboard } = usePortal();
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams<{ id: string }>();
  const editing = location.pathname.endsWith('/new') ? 'new' : id ? Number(id) : null;

  // Hand-off from a save that re-opened the editor (e.g. some images failed to upload).
  const handedNotice = (location.state as { notice?: Notice } | null)?.notice ?? null;
  // A suggestion from the Offer Assistant pre-fills a new offer.
  const handedDraft = editing === 'new' ? (location.state as { draft?: OfferDraft } | null)?.draft ?? null : null;
  const [notice, setNotice] = useState<Notice>(null);
  const [offers, setOffers] = useState<Offer[] | null>(null);
  const [tab, setTab] = useState<Tab>('All');
  const [busyId, setBusyId] = useState<number | null>(null);

  const load = useCallback(
    () => getMyOffers().then(setOffers).catch(() => setNotice({ severity: 'error', text: 'Could not load your offers. Please refresh.' })),
    []
  );

  useEffect(() => {
    load();
  }, [load]);

  // Below the side-by-side breakpoint the editor replaces the list, so bring it into view.
  useEffect(() => {
    if (editing !== null && window.matchMedia('(max-width: 1250px)').matches) window.scrollTo({ top: 0 });
  }, [editing]);

  const counts = Object.fromEntries(TABS.map((t) => [t, (offers ?? []).filter((o) => inTab(o, t)).length])) as Record<Tab, number>;
  const shown = (offers ?? []).filter((o) => inTab(o, tab));

  const closeEditor = () => navigate(to('/partner-account/offers'));

  const handleSaved = (saved: Notice, openOfferId?: number) => {
    load();
    refreshDashboard();
    if (openOfferId) {
      navigate(to(`/partner-account/offers/${openOfferId}/edit`), { replace: true, state: { notice: saved } });
      return;
    }
    setNotice(saved);
    closeEditor();
  };

  const act = async (offer: Offer, action: () => Promise<string>) => {
    setBusyId(offer.id);
    try {
      setNotice({ severity: 'success', text: await action() });
      await load();
    } catch (err) {
      setNotice({ severity: 'error', text: errorMessage(err, 'Something went wrong. Please try again.') });
    } finally {
      setBusyId(null);
    }
  };

  const handleDuplicate = (offer: Offer) => act(offer, async () => (await duplicateOffer(offer.id)).message);

  const handleToggleActive = (offer: Offer) => act(offer, async () => {
    await saveOffer(offer.id, { ...offerToInput(offer), isActive: !offer.isActive });
    return offer.isActive ? 'Offer paused. Customers no longer see it.' : 'Offer resumed.';
  });

  const handleDelete = (offer: Offer) => {
    if (!window.confirm(`Delete "${offer.title}"? Customers will no longer see it. This can't be undone.`)) return;
    act(offer, async () => {
      await deleteOffer(offer.id);
      if (editing === offer.id) closeEditor();
      return 'Offer deleted.';
    });
  };

  // Opens the customer-facing offer page in a new tab; preview=1 keeps it out of the view count.
  const viewLink = (offer: Offer) => (account ? `/offers/resto-${account.restoId}/claim?offer=${offer.id}&preview=1` : '#');

  return (
    <div className={`pp-split ${editing !== null ? 'editing' : ''}`}>
      <div className="pp-split-list">
        <PageHead title="My Offers" subtitle="Create and manage your offers to attract more customers">
          <Link className="pp-btn pp-gold" to={to('/partner-account/offers/new')}><AddIcon />Add New Offer</Link>
        </PageHead>
        <NoticeBox notice={notice} />
        <div className="pp-tabs" role="tablist">
          {TABS.map((t) => (
            <button key={t} role="tab" aria-selected={tab === t} className={`pp-tab ${tab === t ? 'on' : ''}`} onClick={() => setTab(t)}>{t} ({counts[t]})</button>
          ))}
        </div>

        {offers === null ? (
          <p className="pp-muted">Loading offers...</p>
        ) : shown.length === 0 ? (
          <div className="pp-panel pp-empty">
            <h3>{offers.length === 0 ? 'No offers yet' : `No ${tab.toLowerCase()} offers`}</h3>
            <p className="pp-muted">Offers you publish appear on Cheers2Night for customers to claim.</p>
            <Link className="pp-btn pp-gold" to={to('/partner-account/offers/new')}><AddIcon />Add New Offer</Link>
          </div>
        ) : (
          <div className="pp-list">
            {shown.map((offer) => (
              <OfferRow
                key={offer.id}
                offer={offer}
                venueName={account?.name}
                selected={editing === offer.id}
                actions={
                  <>
                    <EditLink to={to(`/partner-account/offers/${offer.id}/edit`)} />
                    {offer.status === 'Active'
                      ? <a className="pp-btn pp-outline pp-sm" href={viewLink(offer)} target="_blank" rel="noreferrer">View</a>
                      : <span className="pp-btn pp-outline pp-sm" aria-disabled="true" title="Only live offers can be viewed by customers">View</span>}
                    <MoreMenu
                      offer={offer}
                      busy={busyId === offer.id}
                      onDuplicate={() => handleDuplicate(offer)}
                      onToggleActive={() => handleToggleActive(offer)}
                      onDelete={() => handleDelete(offer)}
                    />
                  </>
                }
              />
            ))}
          </div>
        )}
      </div>

      <div className="pp-side-col">
        {editing !== null ? (
          <OfferEditor
            key={String(editing)}
            offerId={editing === 'new' ? null : editing}
            onClose={closeEditor}
            onSaved={handleSaved}
            initialNotice={handedNotice}
            draft={handedDraft}
          />
        ) : (
          <section className="pp-editor pp-placeholder pp-desktop-only">
            <h3 style={{ margin: '0 0 8px' }}>Create or edit an offer</h3>
            <p className="pp-muted" style={{ margin: '0 0 16px' }}>Pick an offer's Edit button, or start a new one.</p>
            <Link className="pp-btn pp-gold" to={to('/partner-account/offers/new')}><AddIcon />Add New Offer</Link>
          </section>
        )}
        <VenueGalleryStrip />
      </div>
    </div>
  );
};

export default MyOffersPage;
